import { relativeTime } from "./format";
import { Icon, type IconName } from "./icons";
import { type Draft, clearDraft, draftPlace, draftPreview, parseAddresses, useDrafts } from "./outgoing";
import type { ComposeMode } from "./route";

interface Props {
  /** The draft being written, if one is. */
  selected: string | null;
  onMenu: () => void;
  /** A draft opened to go on writing it. */
  onOpen: (to: { mode: ComposeMode; source: string | null; draft: string }) => void;
  onNew: () => void;
}

const KINDS: Record<ComposeMode, { label: string; icon: IconName }> = {
  new: { label: "New message", icon: "compose" },
  reply: { label: "Reply", icon: "reply" },
  all: { label: "Reply all", icon: "replyAll" },
  forward: { label: "Forward", icon: "forward" },
};

/** The messages left unsent on this device (outgoing.ts: the drafts), the one saved last first: open one to go on
 * writing it and send it, or delete it. They are not in Yahoo's Draft folder, so other devices do not have them. */
export function Drafts({ selected, onMenu, onOpen, onNew }: Props) {
  const drafts = useDrafts();

  const remove = (d: Draft) => {
    if (!confirm(`Delete the draft “${d.subject.trim() || "(no subject)"}”? It has not been sent.`)) return;
    clearDraft(d.id);
  };

  return (
    <div class="offline-settings">
      <div class="toolbar">
        <button class="icon-button menu" title="Folders" onClick={onMenu}>
          <Icon name="menu" />
        </button>
        <h2 class="title">Drafts on this device</h2>
        <span class="spacer" />
        <button class="icon-button" title="New message" onClick={onNew}>
          <Icon name="compose" />
        </button>
      </div>
      <div class="settings-body">
        <p class="muted">
          Messages you left unsent, to finish and send later. They are kept on this device only, not in Yahoo's Draft folder, and stay here until you send or
          delete them.
        </p>
        {drafts.length === 0 ? (
          <p class="muted small">No drafts. Close a message you are writing with ✕ and choose Save draft to keep it here.</p>
        ) : (
          <ul class="device-list outbox-list" role="list">
            {drafts.map((d) => {
              const place = draftPlace(d);
              const kind = KINDS[place.mode];
              const to = parseAddresses(d.to);
              const attached = d.files.length + d.archived.length;
              const preview = draftPreview(d.body);
              return (
                <li key={d.id} class={d.id === selected ? "selected" : undefined}>
                  <Icon name={kind.icon} size={18} title={kind.label} />
                  <button class="outbox-entry draft-open" onClick={() => onOpen({ ...place, draft: d.id })}>
                    <span class="outbox-subject">{d.subject.trim() || "(no subject)"}</span>
                    <span class="muted small">{to.length > 0 ? `To ${to.join(", ")}` : "No recipient yet"}</span>
                    {preview && <span class="draft-preview small">{preview}</span>}
                    <span class="muted small">
                      {kind.label} · saved {relativeTime(new Date(d.savedAt).toISOString())}
                      {attached > 0 && ` · ${attached} ${attached === 1 ? "attachment" : "attachments"}`}
                    </span>
                  </button>
                  <div class="outbox-actions">
                    <button class="text-button" onClick={() => remove(d)}>
                      Delete
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
